import ExecutiveDiagramFrame from './ExecutiveDiagramFrame';

const hubs = [
  {
    id: 'us',
    region: 'Americas',
    venue: 'New York',
    session: 'Premarket through aftermarket',
    instruments: ['Equities', 'Listed options'],
    x: 130,
    color: 'sapphire',
  },
  {
    id: 'eu',
    region: 'Europe',
    venue: 'London · Continental',
    session: 'Listing hours · local currency',
    instruments: ['International equities'],
    x: 310,
    color: 'teal',
  },
  {
    id: 'asia',
    region: 'Asia-Pacific',
    venue: 'Tokyo · Hong Kong',
    session: 'Regional sessions · lunch-aware',
    instruments: ['Regional listings'],
    x: 490,
    color: 'amber',
  },
  {
    id: 'always',
    region: 'Continuous',
    venue: 'UTC always-on',
    session: '24-hour lane',
    instruments: ['Digital assets'],
    x: 670,
    color: 'violet',
  },
];

const palette = (isDark) => ({
  bg: isDark ? '#060d18' : '#f8fafc',
  grid: isDark ? '#1e293b' : '#e2e8f0',
  label: isDark ? '#f8fafc' : '#0f172a',
  muted: isDark ? '#94a3b8' : '#64748b',
  sapphire: isDark ? '#60a5fa' : '#2563eb',
  teal: isDark ? '#2dd4bf' : '#0d9488',
  amber: isDark ? '#fbbf24' : '#d97706',
  violet: isDark ? '#a78bfa' : '#7c3aed',
  emerald: isDark ? '#34d399' : '#059669',
  card: isDark ? '#0f172a' : '#ffffff',
});

const PATiSessionOrchestrationDiagram = ({ theme = 'dark' }) => {
  const isDark = theme === 'dark';
  const c = palette(isDark);
  const colorMap = { sapphire: c.sapphire, teal: c.teal, amber: c.amber, violet: c.violet };

  return (
    <ExecutiveDiagramFrame
      theme={theme}
      caption="Supervised session handoff · scoped state · instrument-aware execution"
      className="pati-sess-frame"
    >
      <svg
        viewBox="0 0 800 400"
        className="w-full h-auto pati-sess-svg"
        role="img"
        aria-label="Global trading session orchestration across Americas, Europe, Asia-Pacific, and continuous markets with supervised handoff"
      >
        <defs>
          <linearGradient id="patiSessRail" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={c.sapphire} stopOpacity="0.15" />
            <stop offset="35%" stopColor={c.teal} stopOpacity="0.35" />
            <stop offset="65%" stopColor={c.amber} stopOpacity="0.35" />
            <stop offset="100%" stopColor={c.violet} stopOpacity="0.2" />
          </linearGradient>
          <filter id="patiSessGlow">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <rect width="800" height="400" fill={c.bg} rx="12" />

        {/* 24h arc */}
        <path
          d="M 80 72 Q 400 20 720 72"
          fill="none"
          stroke={c.grid}
          strokeWidth="1"
          opacity="0.5"
        />
        <path
          className="pati-sess-arc-fill"
          d="M 80 72 Q 400 20 720 72"
          fill="none"
          stroke="url(#patiSessRail)"
          strokeWidth="4"
          strokeLinecap="round"
        />
        <text x="400" y="28" textAnchor="middle" fill={c.muted} fontSize="9" letterSpacing="3" fontWeight="600">
          FOLLOW-THE-CLOCK · 24H MANDATE
        </text>

        {/* Main orchestration rail */}
        <line x1="60" y1="200" x2="740" y2="200" stroke={c.grid} strokeWidth="1" opacity="0.4" />
        <line x1="60" y1="200" x2="740" y2="200" stroke="url(#patiSessRail)" strokeWidth="3" strokeLinecap="round" opacity="0.7" />

        {/* Handoff pulse — animated via transform on wrapper group */}
        <g className="pati-sess-handoff" style={{ transformOrigin: '130px 200px' }}>
          <circle cx="130" cy="200" r="7" fill={c.teal} filter="url(#patiSessGlow)" />
        </g>

        {/* Supervision band */}
        <rect
          className="pati-sess-supervision"
          x="70"
          y="168"
          width="660"
          height="64"
          rx="32"
          fill="none"
          stroke={c.emerald}
          strokeWidth="1"
          strokeDasharray="10 14"
          opacity="0.35"
        />
        <text x="400" y="158" textAnchor="middle" fill={c.emerald} fontSize="8" letterSpacing="2" fontWeight="700">
          SUPERVISED HANDOFF · NO CROSS-MARKET BLEED
        </text>

        {/* Hub nodes */}
        {hubs.map((hub) => {
          const accent = colorMap[hub.color];
          return (
            <g key={hub.id} className={`pati-sess-hub pati-sess-hub-${hub.id}`}>
              {/* Connector */}
              <line x1={hub.x} y1="200" x2={hub.x} y2="248" stroke={accent} strokeWidth="1.5" opacity="0.5" />
              <circle cx={hub.x} cy="200" r="10" fill={c.card} stroke={accent} strokeWidth="2" />
              <circle cx={hub.x} cy="200" r="4" fill={accent} className={`pati-sess-node pati-sess-node-${hub.id}`} />

              {/* Card */}
              <rect
                x={hub.x - 72}
                y="248"
                width="144"
                height="128"
                rx="12"
                fill={c.card}
                stroke={accent}
                strokeWidth="1.5"
                opacity="0.95"
              />
              <rect x={hub.x - 72} y="248" width="144" height="4" rx="2" fill={accent} opacity="0.85" />

              <text x={hub.x} y="272" textAnchor="middle" fill={accent} fontSize="10" fontWeight="800" letterSpacing="1.5">
                {hub.region.toUpperCase()}
              </text>
              <text x={hub.x} y="290" textAnchor="middle" fill={c.label} fontSize="11" fontWeight="600">
                {hub.venue}
              </text>
              <text x={hub.x} y="308" textAnchor="middle" fill={c.muted} fontSize="8">
                {hub.session}
              </text>

              {/* Instrument pills */}
              {hub.instruments.map((inst, i) => {
                const count = hub.instruments.length;
                const pillW = 54;
                const gap = 6;
                const totalW = count * pillW + (count - 1) * gap;
                const startX = hub.x - totalW / 2;
                const px = startX + i * (pillW + gap);
                return (
                  <g key={inst}>
                    <rect
                      x={px}
                      y="318"
                      width={pillW}
                      height="18"
                      rx="9"
                      fill={isDark ? '#1e293b' : '#f1f5f9'}
                      stroke={accent}
                      strokeWidth="0.5"
                      opacity="0.9"
                    />
                    <text x={px + pillW / 2} y="330" textAnchor="middle" fill={accent} fontSize="7" fontWeight="600">
                      {inst}
                    </text>
                  </g>
                );
              })}
            </g>
          );
        })}

        {/* Handoff arrows between hubs */}
        {[
          { x1: 202, x2: 238, label: 'Handoff' },
          { x1: 382, x2: 418, label: 'Handoff' },
          { x1: 562, x2: 598, label: 'Handoff' },
        ].map(({ x1, x2, label }, i) => (
          <g key={i} className={`pati-sess-arrow pati-sess-arrow-${i}`}>
            <line x1={x1} y1="200" x2={x2} y2="200" stroke={c.teal} strokeWidth="1" strokeDasharray="4 4" opacity="0.6" />
            <polygon points={`${x2},197 ${x2 + 6},200 ${x2},203`} fill={c.teal} opacity="0.7" />
            <text x={(x1 + x2) / 2} y="188" textAnchor="middle" fill={c.muted} fontSize="7" letterSpacing="1">
              {label}
            </text>
          </g>
        ))}

        {/* Return loop hint */}
        <path
          d="M 670 248 Q 400 360 130 248"
          fill="none"
          stroke={c.violet}
          strokeWidth="1"
          strokeDasharray="6 8"
          opacity="0.35"
          className="pati-sess-loop"
        />
        <text x="400" y="378" textAnchor="middle" fill={c.muted} fontSize="8" letterSpacing="1.5">
          Cycle completes · state scoped per venue
        </text>
      </svg>
    </ExecutiveDiagramFrame>
  );
};

export default PATiSessionOrchestrationDiagram;
